import type { XAXisComponentOption, YAXisComponentOption } from 'echarts';

import type {
  TimeRangeLane,
  TimeSeries,
  TimeSeriesChartHighlight,
  ValueAxisScales,
} from '@/shared/charts/time-series-chart/TimeSeriesChart.types';
import type { TimeSeriesChartTheme } from '@/shared/charts/time-series-chart/theme/TimeSeriesChartTheme.types';

import { PLOT_GRID_INDEX } from '../config/chartLayout';
import { guideLineVisuals, plotVisuals } from '../config/chartVisuals';
import { buildSliderPreviewOption } from '../navigation/buildSliderPreviewOption';
import type { ChartFormatters } from '../types/ChartFormatters.types';
import type { LineViewport } from '../types/LineViewport.types';
import type { CompleteOptionSection, OptionSection } from '../types/OptionSection.types';
import type { TimeDomain } from '../types/TimeDomain.types';
import { composeOptionSections } from '../utils/composeOptionSections';
import { buildLaneHighlightOption } from './buildLaneHighlightOption';
import { buildLineSeriesOption } from './buildLineSeriesOption';
import { buildLonePointsOption } from './buildLonePointsOption';
import { getLineBucketCount } from './level-of-detail/getLineBucketCount';
import { getSymbolSeriesIds } from './level-of-detail/getSymbolSeriesIds';

/** The guides' axis comes first in the plot, so every other plot section can find it at index 0. */
const SPLIT_LINE_Y_AXIS_INDEX = 0;

type BuildPlotOptionParams = {
  series: TimeSeries[];
  highlightedLanes: TimeRangeLane[];
  highlight?: TimeSeriesChartHighlight;
  domain: TimeDomain;
  isGuideLineEnabled: boolean;
  viewport: LineViewport;
  previewSeriesId: string | undefined;
  /** Only with a slider on screen is there anything to preview, so the hidden series is added only then. */
  hasSliderPreview: boolean;
  valueAxisScales: ValueAxisScales | undefined;
  formatters: ChartFormatters;
  theme: TimeSeriesChartTheme;
};

function buildGuideLineAxisPointer(
  formatters: ChartFormatters,
  theme: TimeSeriesChartTheme
): XAXisComponentOption['axisPointer'] {
  return {
    show: true,
    type: 'line',
    snap: false,
    label: {
      show: true,
      formatter: ({ value }) => formatters.time.format(Number(value)),
      backgroundColor: theme.guideLine.labelBackground,
      color: theme.guideLine.labelColor,
      fontFamily: theme.guideLine.labelFontFamily,
      fontWeight: theme.guideLine.labelFontWeight,
      fontSize: theme.guideLine.labelFontSize,
      lineHeight: theme.guideLine.labelLineHeight,
      borderRadius: theme.guideLine.labelBorderRadius,
      padding: [...guideLineVisuals.labelPadding],
      margin: guideLineVisuals.labelMargin,
    },
    lineStyle: {
      color: theme.guideLine.color,
      width: guideLineVisuals.lineWidth,
      type: guideLineVisuals.lineType,
    },
  };
}

/** Full date + time on every tick, in the chart's locale, independent of zoom level. */
function buildPlotXAxis(
  [min, max]: TimeDomain,
  isGuideLineEnabled: boolean,
  formatters: ChartFormatters,
  theme: TimeSeriesChartTheme
): XAXisComponentOption {
  return {
    type: 'time',
    gridIndex: PLOT_GRID_INDEX,
    // Pinned, so the plot and the track below it always span exactly the same time.
    min,
    max,
    axisLabel: {
      formatter: (value: number) => formatters.dateTime.format(value),
      hideOverlap: true,
      color: theme.axis.labelColor,
      margin: plotVisuals.axisLabelMargin,
    },
    axisLine: { show: false },
    axisTick: { show: false },
    ...(isGuideLineEnabled && {
      axisPointer: buildGuideLineAxisPointer(formatters, theme),
    }),
  };
}

/**
 * The lines sit on hidden axes of their own axis groups, so none of them can draw the horizontal guides
 * without implying a scale. This extra axis carries the guides alone: fixed 0..1 range, no labels.
 */
function buildSplitLineYAxis(theme: TimeSeriesChartTheme): YAXisComponentOption {
  return {
    type: 'value',
    gridIndex: PLOT_GRID_INDEX,
    min: 0,
    max: 1,
    interval: 1 / plotVisuals.splitLineCount,
    axisLabel: { show: false },
    axisLine: { show: false },
    axisTick: { show: false },
    splitLine: {
      show: true,
      lineStyle: {
        color: theme.axis.splitLineColor,
        width: 1,
      },
    },
  };
}

/** Invisible 0..1 axis for series that only carry markAreas and have no values of their own. */
function buildCarrierYAxis(): YAXisComponentOption {
  return {
    type: 'value',
    gridIndex: PLOT_GRID_INDEX,
    min: 0,
    max: 1,
    show: false,
  };
}

function buildSliderPreviewSection(
  series: TimeSeries[],
  previewSeriesId: string | undefined,
  domain: TimeDomain,
  viewport: LineViewport
): OptionSection {
  // First among the series on purpose: ECharts binds the slider's shadow to the first line
  // series it finds on the zoomed axis, once, at creation — see buildSliderPreviewOption.
  return buildSliderPreviewOption({
    previewSeries: previewSeriesId ? series.find(({ id }) => id === previewSeriesId) : undefined,
    domain,
    bucketCount: getLineBucketCount(viewport.plotWidthPx),
  });
}

function buildLaneHighlightSection(highlightedLanes: TimeRangeLane[], theme: TimeSeriesChartTheme): OptionSection {
  return {
    yAxis: [buildCarrierYAxis()],
    series: [buildLaneHighlightOption(highlightedLanes, 0, theme)],
  };
}

/**
 * The plot, as one option section on its own time axis. Its parts, in drawing order: the guides'
 * axis (behind every data series), the hidden slider preview, the lines on their group axes, the
 * lone points (on the guides' fixed 0..1 axis: the markers carry their own height) and the lane
 * highlight bands.
 */
export function buildPlotOption({
  series,
  highlightedLanes,
  highlight,
  domain,
  isGuideLineEnabled,
  viewport,
  previewSeriesId,
  hasSliderPreview,
  valueAxisScales,
  formatters,
  theme,
}: BuildPlotOptionParams): CompleteOptionSection {
  const lines = buildLineSeriesOption({
    series,
    highlightedSeriesIds: highlight?.seriesIds,
    viewport,
    // Decided across every line at once, because the symbol cap is shared by all of them.
    symbolSeriesIds: getSymbolSeriesIds(series, viewport),
    valueAxisScales,
    theme,
  });

  // Present whenever a line can break, even with no lone point in view, so panning does not change
  // the option's structure.
  const canLinesBreak = series.some(({ gapThresholdMs }) => gapThresholdMs !== undefined);

  const plotSections = composeOptionSections([
    { yAxis: [buildSplitLineYAxis(theme)] },
    hasSliderPreview ? buildSliderPreviewSection(series, previewSeriesId, domain, viewport) : undefined,
    { yAxis: lines.yAxis, series: lines.series },
    canLinesBreak ? { series: [buildLonePointsOption(lines.lonePoints, SPLIT_LINE_Y_AXIS_INDEX)] } : undefined,
    highlightedLanes.length > 0 ? buildLaneHighlightSection(highlightedLanes, theme) : undefined,
  ]);

  return { ...plotSections, xAxis: [buildPlotXAxis(domain, isGuideLineEnabled, formatters, theme)] };
}
